import {Inertia} from '@inertiajs/inertia'
import {usePage} from '@inertiajs/inertia-vue3'

const state = () => ({
    formName: '',
    formType: '',
    form: {},
    formData: {},
    errors: {},
    saving: false,
    parent: undefined,
    inlineParent: undefined,
    checkDuplicatesOn: [],
    duplicates: [],
    duplicate: false,
    inlineFormObject: '',
    inlineFormType: '',
    inlineFormPage: '',
    inlineForm: {},
    inlineErrors: {},
    inlineSaving: false,
    inlineCreating: false,
    inlineCreate: {
        grid: undefined
    },
    inlineEditing: false,
    inlineEdit: {
        grid: undefined,
        row: undefined
    },
    bulkUpdateFormName: '',
    bulkUpdateRecordIDs: [],
    bulkUpdateForm: {},
    bulkUpdateErrors: {},
    bulkUpdateSaving: false,
})

const getters = {
    hasInlineErrors: (state) => {
        return Object.keys(state.inlineErrors)?.length > 0 ?? false
    },
    hasError: (state) => (field, inline, bulkUpdate) => {
        if (inline) {
            return field in state.inlineErrors
        }

        if (bulkUpdate) {
            return field in state.bulkUpdateErrors
        }

        return field in state.errors
    },
    showModalForm: (state, getters) => (modal) => {
        return modal && (getters.hasInlineErrors || Object.keys(state.formData).length > 0)
    },
}

const mutations = {
    setFormName(state, {name}) {
        state.formName = name
    },
    setFormType(state, {type}) {
        state.formType = type
    },
    setCheckDuplicatesOn(state, {checkDuplicatesOn}) {
        state.checkDuplicatesOn = checkDuplicatesOn
    },
    setForm(state, {form}) {
        state.form = form
    },
    setFormData(state, {data}) {
        const inlineFormValues = Object.fromEntries(
            Object.entries(state.inlineForm).filter(([key, value]) => {
                return value !== undefined && value !== null
            })
        )

        if ('item' in data) {
            data.item = {...data.item, ...inlineFormValues}
        }

        state.formData = data
    },
    setFormValue({form, errors}, {field, value}) {
        form[field] = value

        if (field in errors) {
            delete errors[field]
        }
    },
    setErrors(state, {errors}) {
        usePage().props.value.flash.error ??= 'Please provide all required information.'
        state.errors = errors
    },
    setSaving(state, {saving}) {
        state.saving = saving
    },
    setFormParent(state, {parent}) {
        state.parent = parent
    },
    setInlineParent(state, {parent}) {
        state.inlineParent = parent
    },
    setDuplicates(state, {duplicates}) {
        state.duplicates = duplicates
    },
    setDuplicate(state, {duplicate}) {
        state.duplicate = duplicate
    },
    setInlineFormObject(state, {object}) {
        state.inlineFormObject = object
    },
    setInlineFormType(state, {type}) {
        state.inlineFormType = type
    },
    setInlineFormPage(state, {page}) {
        state.inlineFormPage = page
    },
    setInlineForm(state, {form}) {
        state.inlineForm = form
    },
    setInlineFormValue({inlineForm, inlineErrors}, {field, value, clear = true}) {
        inlineForm[field] = value

        if (field in inlineErrors && clear) {
            delete inlineErrors[field]
        }
    },
    setInlineErrors(state, {errors}) {
        usePage().props.value.flash.error ??= 'Please provide all required information.'
        state.inlineErrors = errors
    },
    setInlineSaving(state, {saving}) {
        state.inlineSaving = saving
    },
    setInlineCreating(state, {creating}) {
        state.inlineForm = {}
        state.inlineCreating = creating

        state.inlineEditing = false
        state.inlineEdit.grid = undefined
        state.inlineEdit.row = undefined
    },
    setInlineCreate(state, {grid}) {
        state.inlineCreate.grid = grid
    },
    setInlineEditing(state, {editing}) {
        state.inlineForm = {}
        state.inlineEditing = editing

        state.inlineCreating = false
        state.inlineCreate.grid = undefined
    },
    setInlineEdit(state, {grid, row}) {
        state.inlineEdit.grid = grid
        state.inlineEdit.row = row
    },
    resetFormStore(state) {
        state.formName = ''
        state.formType = ''
        state.form = {}
        state.formData = {}
        state.errors = {}
        state.saving = false
        state.parent = undefined
        state.inlineParent = undefined
        state.checkDuplicatesOn = []
        state.duplicates = []
        state.duplicate = false
        state.inlineFormObject = ''
        state.inlineFormType = ''
        state.inlineFormPage = ''
        state.inlineForm = {}
        state.inlineErrors = {}
        state.inlineSaving = false
        state.inlineCreating = false
        state.inlineCreate = {
            grid: undefined
        }
        state.inlineEditing = false
        state.inlineEdit = {
            grid: undefined,
            row: undefined
        }
    },
    setBulkUpdateFormName(state, {name}) {
        state.bulkUpdateFormName = name
    },
    setBulkUpdateRecordIDs(state, {recordIDs}) {
        state.bulkUpdateRecordIDs = recordIDs
    },
    setBulkUpdateFormValue({bulkUpdateForm, bulkUpdateErrors}, {field, value, clear = true}) {
        bulkUpdateForm[field] = value

        if (bulkUpdateForm[field] === '' || bulkUpdateForm[field] === null || bulkUpdateForm[field] === undefined) {
            delete bulkUpdateForm[field]
        }

        if (field in bulkUpdateErrors && clear) {
            delete bulkUpdateErrors[field]
        }
    },
    setBulkUpdateErrors(state, {errors}) {
        state.bulkUpdateErrors = errors
    },
    setBulkUpdateSaving(state, {saving}) {
        state.bulkUpdateSaving = saving
    },
    resetBulkUpdateFormData(state) {
        state.bulkUpdateForm = {}
    },
    resetBulkUpdateFormStore(state) {
        state.bulkUpdateFormName = ''
        state.bulkUpdateRecordIDs = []
        state.bulkUpdateForm = {}
        state.bulkUpdateErrors = {}
        state.bulkUpdateSaving = false
    }
}

const actions = {
    async saveCreateForm({commit, dispatch, state}) {
        await dispatch('checkForDuplicates')

        if (state.duplicates.length > 0 && state.duplicate === false) {
            commit('setSaving', {saving: false})
            return
        }

        Inertia.post(route('record.store', state.formName), {...state.form}, {
            onSuccess: () => {
                commit('setSaving', {saving: false})
                commit('setDuplicate', {duplicate: false})
            },
            onError: errors => {
                commit('setSaving', {saving: false})
                commit('setErrors', {errors})
            }
        })
    },
    saveEditForm({commit, state}) {
        Inertia.put(route('record.update', {form: state.formName, record_id: state.form.id}), {...state.form}, {
            onSuccess: () => {
                commit('setSaving', {saving: false})
            },
            onError: errors => {
                commit('setSaving', {saving: false})
                commit('setErrors', {errors})
            }
        })
    },
    saveBulkUpdateForm({commit, state}) {
        Inertia.post(route('records.bulk-update', {form: state.bulkUpdateFormName}), {
            recordIDs: state.bulkUpdateRecordIDs,
            ...state.bulkUpdateForm
        }, {
            onSuccess: () => {
                commit('setBulkUpdateSaving', {saving: false})
                commit('setBulkUpdateErrors', {errors: {}})
            },
            onError: errors => {
                commit('setBulkUpdateSaving', {saving: false})
                commit('setBulkUpdateErrors', {errors})
            }
        })
    },
    saveBulkUpdateForAllRecords({commit, state}) {
        Inertia.post(route('records.bulk-update-all', {form: state.bulkUpdateFormName}), {
            ...state.bulkUpdateForm
        }, {
            onSuccess: () => {
                commit('setBulkUpdateSaving', {saving: false})
                commit('setBulkUpdateErrors', {errors: {}})
            },
            onError: errors => {
                commit('setBulkUpdateSaving', {saving: false})
                commit('setBulkUpdateErrors', {errors})
            }
        })
    },
    async saveInlineCreateForm({commit, dispatch, state, getters}, {modal}) {
        try {
            const {data} = await axios.post(
                route('ajax.inline.record.store', {
                    form: state.inlineFormObject,
                    modal: getters.showModalForm(modal)
                }),
                state.inlineForm
            )

            commit('setInlineSaving', {saving: false})
            commit('setInlineErrors', {errors: {}})
            commit('setFormData', {data: {}})

            usePage().props.value.flash.error = null
            usePage().props.value.flash.success ??= data.message
        } catch (error) {
            await dispatch('loadForm')

            commit('setInlineSaving', {saving: false})

            if (error.response.status === 422) {
                commit('setInlineErrors', {errors: error.response.data.errors})
            }

            if (error.response.status === 500) {
                commit('setInlineErrors', {errors: error.response.data.message})
                usePage().props.value.flash.error ??= 'There was an error when saving, please try again.'
            }
        }
    },
    async saveInlineEditForm({commit, dispatch, state, getters}, {modal}) {
        try {
            const {data} = await axios.patch(route('ajax.inline.record.update', {
                form: state.inlineFormObject,
                record_id: state.inlineForm.id,
                modal: getters.showModalForm(modal)
            }), state.inlineForm)

            commit('setInlineSaving', {saving: false})
            commit('setInlineErrors', {errors: {}})
            commit('setFormData', {data: {}})
            commit('setInlineEditing', {editing: false})
            commit('setInlineEdit', {
                grid: undefined,
                row: undefined
            })

            usePage().props.value.flash.error = null
            usePage().props.value.flash.success ??= data.message
        } catch (error) {
            await dispatch('loadForm')

            commit('setInlineSaving', {saving: false})

            if (error.response.status === 422) {
                commit('setInlineErrors', {errors: error.response.data.errors})
            }
        }
    },
    async checkForDuplicates({commit, state}) {
        if (state.checkDuplicatesOn.length === 0 || state.duplicate === true) {
            return
        }

        try {
            const {data} = await axios.get(route('ajax.object.duplicate-check', {
                object: state.formName,
                fields: state.checkDuplicatesOn.map(field => {
                    return {
                        name: field,
                        value: state.form[field]
                    }
                })
            }))

            commit('setDuplicates', {duplicates: data.duplicates})
            commit('setDuplicate', {duplicate: false})
        } catch {
            console.log('duplicate check failed')
        }
    },
    async loadForm({state, commit}) {
        const formRoute = state.inlineFormType === 'CreateForm'
            ? 'ajax.inline.record.create'
            : 'ajax.inline.record.edit'

        const params = {
            page: state.inlineFormPage
        }

        if (state.inlineFormType === 'EditForm') {
            params.record_id = state.inlineForm.id
        }

        const {data} = await axios.get(route(formRoute, params))
        commit('setFormData', {data: data})
    }
}

export default {
    state,
    getters,
    mutations,
    actions
}
